import React, { useState, useEffect } from 'react';
import {
  createProfileListing,
  uploadImage,
  getZodiac,
  getStar,
  getCountries,
  getStates,
  getCities,
  getReligions,
  getCastes,
  getOccupations,
  getMotherTongue,
} from '../utils/api';
import MatrimonyHeader from '../components/MatrimonyHeader';
import ProfileListingHero from '../components/ProfileListingHero';
import { ClipLoader } from 'react-spinners';

export default function ProfileListing() {
  const [options, setOptions] = useState({
    zodiac: [],
    stars: [],
    countries: [],
    states: [],
    cities: [],
    religions: [],
    castes: [],
    occupations: [],
    motherTongues: [],
  });

  const [formValues, setFormValues] = useState({
    fullName: '',
    dob: '',
    gender: '',
    religion: '',
    occupation: '',
    annualIncome: '',
    caste: '',
    motherTongue: '',
    zodiacSign: '',
    star: '',
    maritalStatus: '',
    country: '',
    state: '',
    city: '',
    address: '',
    description: '',
  });

  const [filePreviews, setFilePreviews] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [images, setImages] = useState([]);
  const [profileVisibility, setProfileVisibility] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [
          zodiac,
          stars,
          countries,
          states,
          cities,
          religions,
          castes,
          occupations,
          motherTongues,
        ] = await Promise.all([
          getZodiac(),
          getStar(),
          getCountries(),
          getStates(),
          getCities(),
          getReligions(),
          getCastes(),
          getOccupations(),
          getMotherTongue(),
        ]);

        setOptions({
          zodiac,
          stars,
          countries,
          states,
          cities,
          religions,
          castes,
          occupations,
          motherTongues,
        });
      } catch (err) {
        console.error('Failed to load options', err);
      }
    })();
  }, []);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormValues((prev) => ({ ...prev, [id]: value }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    const previews = files.map((file) => URL.createObjectURL(file));
    setFilePreviews(previews);

    const uploadedUrls = [];
    const progressState = {};

    for (let idx = 0; idx < files.length; idx++) {
      const file = files[idx];
      try {
        const result = await uploadImage(file, (percent) => {
          setUploadProgress((prev) => ({ ...prev, [idx]: percent }));
        });
        uploadedUrls.push(result.url);
      } catch (err) {
        console.error('Image upload failed:', err);
        setUploadProgress((prev) => ({ ...prev, [idx]: 0 }));
      }
    }

    setImages(uploadedUrls);
  };

  const removeImage = (index) => {
    setFilePreviews((prev) => prev.filter((_, i) => i !== index));
    setImages((prev) => prev.filter((_, i) => i !== index));
    setUploadProgress((prev) => {
      const newProg = { ...prev };
      delete newProg[index];
      return newProg;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        ...formValues,
        images,
        visibility: profileVisibility ? 'Public' : 'Private',
      };
      await createProfileListing(payload);
    } catch (err) {
      console.error('Submission failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <MatrimonyHeader />
      <ProfileListingHero />
      <form onSubmit={handleSubmit} className="min-h-screen p-4 md:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-semibold text-amber-700 text-center mb-8">
            Let's showcase the groom's or bride's details
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">

            {/* Full Name */}
            <div className="space-y-2">
              <label htmlFor="fullName" className="text-sm font-medium text-gray-700">Full Name</label>
              <input id="fullName" type="text" value={formValues.fullName} onChange={handleChange} placeholder="Enter Name" className="w-full border rounded px-3 py-2" />
            </div>

            {/* Date of Birth */}
            <div className="space-y-2">
              <label htmlFor="dob" className="text-sm font-medium text-gray-700">Date of Birth</label>
              <input id="dob" type="date" value={formValues.dob} onChange={handleChange} className="w-full border rounded px-3 py-2" />
            </div>

            {/* Gender */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Gender</label>
              <select id="gender" value={formValues.gender} onChange={handleChange} className="w-full border rounded px-3 py-2">
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Religion */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Religion</label>
              <select id="religion" value={formValues.religion} onChange={handleChange} className="w-full border rounded px-3 py-2">
                <option value="">Choose Religion</option>
                {options.religions.map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}
              </select>
            </div>

            {/* Caste */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Caste</label>
              <select id="caste" value={formValues.caste} onChange={handleChange} className="w-full border rounded px-3 py-2">
                <option value="">Choose Caste</option>
                {options.castes.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>

            {/* Occupation */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Occupation</label>
              <select id="occupation" value={formValues.occupation} onChange={handleChange} className="w-full border rounded px-3 py-2">
                <option value="">Choose Occupation</option>
                {options.occupations.map((o) => <option key={o._id} value={o._id}>{o.name}</option>)}
              </select>
            </div>

            {/* Annual Income */}
            <div className="space-y-2">
              <label htmlFor="annualIncome" className="text-sm font-medium text-gray-700">Annual Income</label>
              <input id="annualIncome" type="text" value={formValues.annualIncome} onChange={handleChange} placeholder="Enter Annual Income" className="w-full border rounded px-3 py-2" />
            </div>

            {/* Mother Tongue */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Mother Tongue</label>
              <select id="motherTongue" value={formValues.motherTongue} onChange={handleChange} className="w-full border rounded px-3 py-2">
                <option value="">Choose Mother Tongue</option>
                {options.motherTongues.map((mt) => <option key={mt._id} value={mt._id}>{mt.name}</option>)}
              </select>
            </div>

            {/* Zodiac Sign */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Zodiac Sign</label>
              <select id="zodiacSign" value={formValues.zodiacSign} onChange={handleChange} className="w-full border rounded px-3 py-2">
                <option value="">Choose Zodiac Sign</option>
                {options.zodiac.map((z) => <option key={z._id} value={z._id}>{z.name}</option>)}
              </select>
            </div>

            {/* Star */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Star</label>
              <select id="star" value={formValues.star} onChange={handleChange} className="w-full border rounded px-3 py-2">
                <option value="">Choose Star</option>
                {options.stars.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
              </select>
            </div>

            {/* Marital Status */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Marital Status</label>
              <select id="maritalStatus" value={formValues.maritalStatus} onChange={handleChange} className="w-full border rounded px-3 py-2">
                <option value="">Choose Marital Status</option>
                <option value="Single">Single</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
                <option value="Separated">Separated</option>
              </select>
            </div>

            {/* Country */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Country</label>
              <select id="country" value={formValues.country} onChange={handleChange} className="w-full border rounded px-3 py-2">
                <option value="">Choose Country</option>
                {options.countries.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>

            {/* State */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">State</label>
              <select id="state" value={formValues.state} onChange={handleChange} className="w-full border rounded px-3 py-2">
                <option value="">Choose State</option>
                {options.states.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
              </select>
            </div>

            {/* City */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">City</label>
              <select id="city" value={formValues.city} onChange={handleChange} className="w-full border rounded px-3 py-2">
                <option value="">Choose City</option>
                {options.cities.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>

            {/* Profile Visibility */}
            <div className="space-y-2 flex items-center">
              <input type="checkbox" checked={profileVisibility} onChange={() => setProfileVisibility((v) => !v)} className="w-5 h-5" />
              <span className="ml-2 text-sm font-medium text-gray-700">{profileVisibility ? 'Public' : 'Private'}</span>
            </div>

            {/* Address */}
            <div className="space-y-2 col-span-full">
              <label className="text-sm font-medium text-gray-700">Address</label>
              <input id="address" type="text" value={formValues.address} onChange={handleChange} placeholder="Enter address" className="w-full border rounded px-3 py-2" />
            </div>

            {/* Image Upload */}
            <div className="col-span-full">
              <label className="text-sm font-medium text-gray-700">Upload Images</label>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                className="w-full border rounded px-3 py-2"
              />
              <div className="mt-4 grid grid-cols-3 gap-4">
                {filePreviews.map((url, idx) => (
                  <div key={idx} className="relative">
                    <img
                      src={url}
                      alt={`preview-${idx}`}
                      className="w-full h-32 object-cover rounded"
                    />

                    {/* Circular Progress Overlay */}
                    {uploadProgress[idx] != null && uploadProgress[idx] < 100 && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="relative w-12 h-12">
                          <svg viewBox="0 0 36 36" className="w-full h-full">
                            <path
                              className="text-gray-300"
                              fill="none"
                              strokeWidth="3"
                              d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831"
                            />
                            <path
                              className="text-[#E7603C]"
                              fill="none"
                              strokeWidth="3"
                              strokeDasharray={`${uploadProgress[idx]}, 100`}
                              d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831"
                            />
                          </svg>
                          <span className="absolute inset-0 flex items-center justify-center text-sm text-white">
                            {uploadProgress[idx]}%
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Delete Button */}
                    <button
                      type="button"
                      onClick={() => removeImage(idx)}
                      className="absolute top-1 right-1 bg-red-500 text-white bg-opacity-75 px-1 rounded-full hover:bg-opacity-100"
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2 col-span-full">
              <label className="text-sm font-medium text-gray-700">Description</label>
              <textarea id="description" value={formValues.description} onChange={handleChange} placeholder="Enter Description" className="w-full border rounded px-3 py-2" rows={3} />
            </div>

            {/* Submit Button */}
            <div className="col-span-full text-right">
              <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center space-x-2">
                {isSubmitting && <ClipLoader size={20} />}
                <span>{isSubmitting ? 'Submitting...' : 'Submit'}</span>
              </button>
            </div>

          </div>
        </div>
      </form>
    </>
  );
}
